import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import './auth.css'

/**
 * The shared pre-login shell — reused by sign-up, reset, the session-unavailable
 * screen, MFA and the two public token routes.
 *
 * **Its own module, where the plan puts it inside `SignIn.tsx`.** Six screens
 * import this shell, and the plan's `SessionUnavailable` opens with
 * `import { AuthCard } from './SignIn'` — which drags the whole sign-in form,
 * its analytics calls and its pointer hook into every one of them, and inverts
 * the dependency: the shared piece would belong to the first screen that
 * happened to need it. A shell six routes share is not sign-in's.
 *
 * `<h1>`, because `BareLayout` renders no `TopBar` and therefore nothing else
 * owns the document heading on these routes. That asymmetry with the chrome
 * routes is deliberate and is what `App.tsx`'s `BarePlaceholder` already
 * encodes.
 *
 * ## Three regions, because the design has three
 *
 * `TrackE5 Web App.dc.html` draws a brand block, a card and a foot as SIBLINGS
 * in one 400px column — the foot is not inside the card. That is why `subtitle`
 * and `foot` are props rather than something a caller renders inside
 * `children`: put the foot in `children` and it lands on `--surface` at the
 * card's 18px gap, when the design has it on the canvas at the column's 22px.
 * Both of those are visible, and the second is a contrast question rather than
 * a spacing one — see the `.auth-foot` note in `auth.css`.
 *
 * Obtained 2026-08-13 from the complete 578,308-byte source; issue 271 records
 * why the truncated copy showed no auth screens at all.
 */
export function AuthCard({
  title,
  subtitle,
  children,
  foot,
  icon,
  centered = false,
}: {
  title: string
  subtitle?: string
  children: ReactNode
  foot?: ReactNode
  /**
   * The 48px tile the design draws ABOVE the heading — a slot, not a child.
   *
   * Passing it through `children` was the obvious thing and it is wrong, which
   * a browser showed and 1838 unit tests did not: `children` render AFTER the
   * head block, so every screen doing it put the icon UNDER its own title while
   * all three comps (`mfa`, `verify`, `expired`) draw it above. jsdom computes
   * no layout and none of those screens' tests asserted DOM order, so it was
   * invisible to the whole suite.
   *
   * A slot rather than asking callers to reorder, because the ordering is a
   * fact about this card's anatomy rather than about any one screen — the same
   * argument `subtitle` makes for not being passed as a child.
   */
  icon?: ReactNode
  /**
   * Centre the card's contents — the design's `mfa` treatment.
   *
   * A prop rather than a class the caller adds to `children`, because the
   * modifier belongs to the CARD element and a caller cannot reach it. Off by
   * default: only one of the twelve pre-login screens is drawn this way.
   */
  centered?: boolean
}) {
  /**
   * The brand block is a LINK, so it is a tap target and M0-R29 applies: 44px
   * below 640px, 33 above. The design draws the mark at 30x30 and the browser
   * sweep measured the anchor at 103x30 — under the floor at every phone width.
   *
   * The mark stays 30px; the ANCHOR grows around it. Growing the mark instead
   * would be redrawing the design to satisfy our rule, when the rule is about
   * the touchable box and the box is free.
   *
   * `useHitTarget`, not a media query: `single-source.test.ts` forbids a
   * component declaring a breakpoint, and an inline `min-height` would beat one
   * anyway — which is exactly why #90 has to go through this map rather than
   * through CSS.
   */
  const brandMinHeight = useHitTarget('page')

  return (
    <div className="auth-page">
      <div className="auth-shell">
        {/*
         * The design wires the brand block to the landing screen. `/` is that
         * destination here: `RedirectWhenSignedIn` already owns where a signed-in
         * visitor goes, so this does not have to know.
         *
         * `aria-hidden` on the mark and a real label on the link, because "t5"
         * followed by "trackE5" is the same word twice to a screen reader.
         */}
        <Link
          to="/"
          className="auth-brand"
          aria-label="TrackE5 home"
          style={{ minHeight: brandMinHeight }}
        >
          <span className="auth-brand__mark" aria-hidden="true">
            t5
          </span>
          <span className="auth-brand__word">trackE5</span>
        </Link>

        <div className={centered ? 'auth-card auth-card--centered' : 'auth-card'}>
          {icon}
          <div className="auth-card__head">
            <h1 className="auth-card__title">{title}</h1>
            {subtitle ? <p className="auth-card__sub">{subtitle}</p> : null}
          </div>
          {children}
        </div>

        {foot ? <p className="auth-foot">{foot}</p> : null}
      </div>
    </div>
  )
}

/**
 * The "or" rule between the primary action and the federated one.
 *
 * A component rather than three divs at each call site: it appears on sign-in
 * and sign-up identically, and the middle word is the only thing that varies
 * between them (it does not, today — but a divider whose label is a prop is
 * the version that does not get copy-pasted when it does).
 */
export function AuthDivider({ label = 'or' }: { label?: string }) {
  return (
    <div className="auth-divider">
      <span className="auth-divider__rule" />
      <span className="auth-divider__label">{label}</span>
      <span className="auth-divider__rule" />
    </div>
  )
}
